import type { WritableSignal } from "@angular/core";
import type { PanelMoveDirection } from "../../../../modules/s25-movable/s25.panel.move.overlay.model";

/** Dashboard panel `typeId` values from {@link DashLangMap} (list style keys + special panels + available-space states). */
export const HomeDashPanelTypeId = {
    UpcomingEvents: 1,
    FavoriteEvents: 11,
    FavoriteLocations: 14,
    FavoriteResources: 16,
    EventDrafts: 51,
    FavoriteEventSearches: 101,
    FavoriteLocationSearches: 104,
    FavoriteResourceSearches: 106,
    FavoriteReports: 1000,
    TasksSummary: 1010,
    QuickSearch: 3000,
    AvailableSpace: 4000,
    /** In-panel state / chooser item under {@link HomeDashPanelTypeId.AvailableSpace}. */
    AvailableSpaceFindLocation: 4001,
    /** In-panel state / chooser item under {@link HomeDashPanelTypeId.AvailableSpace}. */
    AvailableSpaceFindTime: 4002,
    ExpressScheduling: 5000,
    ExpressSchedulingReserveOnly: 5001,
} as const;

export type HomeDashPanelTypeIdValue = (typeof HomeDashPanelTypeId)[keyof typeof HomeDashPanelTypeId];

/** Panel kinds that never use generic `panel-items.json` list bodies. */
export const HOME_DASH_NON_LIST_PANEL_TYPE_IDS: ReadonlySet<number> = new Set([
    HomeDashPanelTypeId.QuickSearch,
    HomeDashPanelTypeId.AvailableSpace,
    HomeDashPanelTypeId.ExpressScheduling,
    HomeDashPanelTypeId.ExpressSchedulingReserveOnly,
]);

/** Dashboard panel view-model shared between Angular dash leaf panels and the panel orchestrator. */
export type HomeDashPanelModel = {
    panelId: number;
    typeId: number;
    isCollapsed?: boolean;
    isInitiallyEmpty?: boolean;
    isAsync?: boolean;
    isLoading?: boolean;
    items?: unknown[];
    [key: string]: unknown;
};

/**
 * Customize / reorder state shared from {@link HomeDashComponent} down to panels.
 * Signal fields so OnPush descendants re-evaluate when customize or selection changes.
 */
export type HomeDashCustomize = {
    onFocus?: (event: FocusEvent) => void;
    onFocusOut?: (event: FocusEvent) => void;
    /** Panel currently selected for keyboard / single-pointer reorder. */
    selectedMovePanelId: WritableSignal<number | undefined>;
    /** Reorder the selected panel (same path as arrow keys). */
    moveSelectedPanel: (direction: PanelMoveDirection) => void;
    toolboxOpen: WritableSignal<boolean>;
};
